import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePolling, useQuote } from "@/hooks/usePolling";
import { useTheme } from "@/context/ThemeContext";
import { Card, StatusBadge, Metric, Pill, SectionLabel } from "@/components/common";
import { AurumOrb } from "@/components/brand/AurumOrb";
import { CandleChart } from "@/components/charts/CandleChart";
import { TradingViewChart } from "@/components/charts/TradingViewChart";
import { SetupScoreGauge } from "@/components/app/SetupScore";
import { TradePlanCard } from "@/components/app/TradePlan";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, ShieldAlert, Scale, Clock, Newspaper, X, Check } from "lucide-react";

function ema(closes, period) {
  if (!closes.length) return [];
  const k = 2 / (period + 1);
  const out = [closes[0]];
  for (let i = 1; i < closes.length; i++) out.push(closes[i] * k + out[i - 1] * (1 - k));
  return out;
}

const TFS = ["5M", "15M", "1H", "4H"];

export default function Dashboard() {
  const [beginner, setBeginner] = useState(true);
  const [tf, setTf] = useState("15M");
  const [chartMode, setChartMode] = useState("live");
  const { theme } = useTheme();
  const { data: snap, refresh } = usePolling(async () => (await api.get(`/analysis/snapshot?beginner=${beginner}`)).data, 15000, [beginner]);
  const { data: candleData } = usePolling(async () => (await api.get(`/market/candles?tf=${tf}&count=140`)).data, 15000, [tf]);
  const { data: liveQuote } = useQuote(5000);
  const { data: paper, refresh: refreshPaper } = usePolling(async () => (await api.get("/paper/account")).data, 8000, []);

  const [explain, setExplain] = useState("");
  const [explaining, setExplaining] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);
  const [showLiveLog, setShowLiveLog] = useState(false);

  if (!snap) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <AurumOrb status="ANALYZING" size={90} />
        <div className="text-sm text-[var(--text-muted)]">ANALYZING MARKET…</div>
      </div>
    );
  }

  const status = snap.status.status;
  const livePrice = liveQuote?.price ?? snap.quote.price;
  const baseCandles = candleData?.candles || [];
  const liveCandles = (() => {
    if (!baseCandles.length || livePrice == null) return baseCandles;
    const cs = baseCandles.slice();
    const last = { ...cs[cs.length - 1] };
    last.c = livePrice;
    last.h = Math.max(last.h, livePrice);
    last.l = Math.min(last.l, livePrice);
    cs[cs.length - 1] = last;
    return cs;
  })();
  const closes = liveCandles.slice(-120).map((c) => c.c);
  const emaLine = ema(closes, 20);

  const overlays = {
    ema: emaLine,
    levels: snap.levels,
    prevDay: snap.prev_day,
    sessionRange: snap.session_range,
    livePrice,
    direction: snap.setup?.direction,
    entryZone: snap.trade?.entry_zone,
    stop: snap.trade?.stop,
    tps: snap.trade ? [snap.trade.tp1, snap.trade.tp2, snap.trade.tp3] : [],
  };

  const runExplain = async () => {
    setExplaining(true);
    try {
      const { data } = await api.post(`/ai/explain?beginner=${beginner}`);
      setExplain(data.text);
    } catch { toast.error("AI temporarily unavailable."); }
    finally { setExplaining(false); }
  };

  const canApprove = status === "ENTRY CONDITIONS SATISFIED" && snap.trade;

  return (
    <div className="space-y-5 animate-fade-up">
      {/* Action strip */}
      <Card className="p-5 relative overflow-hidden grain">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <AurumOrb status={status} size={84} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <SectionLabel>Current Action</SectionLabel>
              <StatusBadge status={status} testId="current-action-badge" />
              {snap.news?.protection_active && <Pill color="#F5B942"><ShieldAlert size={12} /> News Protection</Pill>}
            </div>
            <p className="mt-2 text-[var(--text)] text-base md:text-lg font-medium max-w-2xl" data-testid="current-action-reason">
              {snap.status.reason}
            </p>
            {(() => {
              const D = {
                "ENTRY CONDITIONS SATISFIED": { t: "✅ Do this now: place the trade using the order ticket below — copy the exact entry, stop, and lot size into MT5.", c: "#21C58E" },
                "SETUP FORMING": { t: "⏳ Get ready: a setup is forming. Prepare your order but wait for Aurum to confirm the trigger.", c: "#F5B942" },
                "WAIT": { t: "✋ Do nothing right now. No valid setup — waiting patiently IS the trade.", c: "#98A2B3" },
                "NO TRADE": { t: "✋ Stand aside. Conditions don't fit your plan — protecting capital is the win today.", c: "#98A2B3" },
                "NEWS PROTECTION ACTIVE": { t: "⚠️ Stand down: high-impact news is near. Avoid new entries until it passes.", c: "#F5B942" },
                "MARKET DATA UNAVAILABLE": { t: "⛔ Paused: live data is temporarily unavailable. Do not trade on missing data.", c: "#FF4D67" },
              };
              const d = D[status] || { t: "Review the analysis below before making any decision.", c: "#98A2B3" };
              return (
                <div className="mt-3 rounded-lg px-3 py-2 text-sm font-semibold" data-testid="action-directive"
                  style={{ background: `${d.c}14`, border: `1px solid ${d.c}33`, color: d.c }}>
                  {d.t}
                </div>
              );
            })()}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-full border border-[var(--border2)] p-0.5 text-xs">
              <button onClick={() => setBeginner(true)} className={`px-3 py-1.5 rounded-full transition-colors ${beginner ? "btn-gold font-semibold" : "text-[var(--text-sec)]"}`} data-testid="mode-beginner">Beginner</button>
              <button onClick={() => setBeginner(false)} className={`px-3 py-1.5 rounded-full transition-colors ${!beginner ? "btn-gold font-semibold" : "text-[var(--text-sec)]"}`} data-testid="mode-advanced">Advanced</button>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Chart + trade plan */}
        <div className="lg:col-span-2 space-y-5">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-semibold">XAU/USD</span>
                <span className="tnum text-lg font-semibold">${snap.quote.price?.toFixed(2)}</span>
                <span className={`tnum text-xs ${snap.quote.change >= 0 ? "text-bull" : "text-bear"}`}>
                  {snap.quote.change >= 0 ? "+" : ""}{snap.quote.change} ({snap.quote.change_pct}%)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border border-[var(--border2)] p-0.5">
                  <button onClick={() => setChartMode("live")} data-testid="chart-mode-live"
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${chartMode === "live" ? "bg-[var(--surface)] text-aurum font-semibold" : "text-[var(--text-sec)]"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${chartMode === "live" ? "bg-bull animate-pulse" : "bg-[var(--text-muted)]"}`} />Live
                  </button>
                  <button onClick={() => setChartMode("aurum")} data-testid="chart-mode-aurum"
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${chartMode === "aurum" ? "bg-[var(--surface)] text-aurum font-semibold" : "text-[var(--text-sec)]"}`}>Aurum</button>
                </div>
                <div className="flex rounded-lg border border-[var(--border2)] p-0.5">
                  {TFS.map((t) => (
                    <button key={t} onClick={() => setTf(t)} data-testid={`tf-${t}`}
                      className={`px-2.5 py-1 text-xs rounded-md transition-colors ${tf === t ? "bg-[var(--surface)] text-aurum font-semibold" : "text-[var(--text-sec)]"}`}>{t}</button>
                  ))}
                </div>
              </div>
            </div>
            {chartMode === "live" ? (
              <TradingViewChart tf={tf} theme={theme} height={380} />
            ) : (
              <CandleChart candles={liveCandles} overlays={overlays} height={380} />
            )}
            {chartMode === "aurum" && (
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-[var(--text-muted)]">
                <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm" style={{ background: "#D6AE57" }} /> EMA20 / Entry zone</span>
                <span className="flex items-center gap-1"><span className="h-0.5 w-3" style={{ background: "#EF6262" }} /> Stop</span>
                <span className="flex items-center gap-1"><span className="h-0.5 w-3" style={{ background: "#21C58E" }} /> Targets</span>
                <span className="flex items-center gap-1"><span className="h-0.5 w-3 border-t border-dashed" style={{ borderColor: "#98A2B3" }} /> S/R</span>
                <span className="flex items-center gap-1"><span className="h-0.5 w-3 border-t border-dashed" style={{ borderColor: "#60A5FA" }} /> Prev day H/L</span>
                <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm" style={{ background: "rgba(139,92,246,0.5)" }} /> Asia range</span>
                <span className="flex items-center gap-1"><span className="h-0.5 w-3" style={{ background: "#EAB308" }} /> Live price</span>
              </div>
            )}
            {(snap.prev_day || snap.session_range) && (() => {
              const price = snap.quote.price;
              const dist = (lvl) => {
                if (price == null || lvl == null) return "";
                const d = lvl - price;
                return `${d >= 0 ? "+" : "−"}$${Math.abs(d).toFixed(2)} vs price`;
              };
              const cards = [];
              if (snap.prev_day) {
                cards.push({ key: "prev-day-high", label: "Prev Day High", val: snap.prev_day.high, role: "resistance", color: "#60A5FA" });
                cards.push({ key: "prev-day-low", label: "Prev Day Low", val: snap.prev_day.low, role: "support", color: "#60A5FA" });
              }
              if (snap.session_range) {
                const tag = snap.session_range.complete ? "Asian range" : "Asian range (forming)";
                cards.push({ key: "asia-high", label: "Asian High", val: snap.session_range.high, role: `${tag} · break long`, color: "#8B5CF6" });
                cards.push({ key: "asia-low", label: "Asian Low", val: snap.session_range.low, role: `${tag} · break short`, color: "#8B5CF6" });
              }
              return (
                <div className="mt-3 grid grid-cols-2 gap-3" data-testid="prev-day-levels">
                  {cards.map((c) => (
                    <div key={c.key} className="rounded-lg border border-[var(--border2)] p-2.5" data-testid={c.key}>
                      <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.label}
                      </div>
                      <div className="tnum text-sm font-semibold mt-0.5">${c.val?.toFixed(2)}</div>
                      <div className="text-[10px] text-[var(--text-muted)] tnum">{dist(c.val)} · {c.role}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </Card>

          <TradePlanCard setup={snap.setup} trade={snap.trade} sizing={snap.sizing} dynamicRisk={snap.dynamic_risk} entryGuard={snap.entry_guard} capitalGuard={snap.capital_guard} />

          {/* Devil's advocate */}
          <Card className="p-5" data-testid="devils-advocate">
            <div className="flex items-center gap-2 mb-3">
              <Scale size={16} className="text-aurum" />
              <SectionLabel>Devil's Advocate</SectionLabel>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-bull mb-2">CASE FOR THE TRADE</div>
                <ul className="space-y-1.5">
                  {snap.devil.case_for.length ? snap.devil.case_for.map((x, i) => (
                    <li key={i} className="text-xs text-[var(--text-sec)] flex gap-2"><Check size={13} className="text-bull mt-0.5 shrink-0" />{x}</li>
                  )) : <li className="text-xs text-[var(--text-muted)]">None yet.</li>}
                </ul>
              </div>
              <div>
                <div className="text-xs font-semibold text-bear mb-2">CASE AGAINST THE TRADE</div>
                <ul className="space-y-1.5">
                  {snap.devil.case_against.length ? snap.devil.case_against.map((x, i) => (
                    <li key={i} className="text-xs text-[var(--text-sec)] flex gap-2"><X size={13} className="text-bear mt-0.5 shrink-0" />{x}</li>
                  )) : <li className="text-xs text-[var(--text-muted)]">None.</li>}
                </ul>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[var(--border2)] text-sm">
              <span className="text-[var(--text-muted)] text-xs uppercase tracking-wider">Final assessment: </span>
              <span className="font-semibold">{snap.devil.verdict}</span>
            </div>
          </Card>

          {/* AI explanation */}
          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2"><Sparkles size={16} className="text-aurum" /><SectionLabel>Aurum Read</SectionLabel></div>
              <button onClick={runExplain} disabled={explaining} data-testid="ai-explain-btn"
                className="text-xs rounded-full px-3 py-1.5 border border-aurum text-aurum hover:bg-[rgba(214,174,87,0.1)] transition-colors disabled:opacity-50">
                {explaining ? "Analyzing…" : "Explain this market"}
              </button>
            </div>
            {explain ? (
              <div className="text-sm text-[var(--text-sec)] whitespace-pre-wrap leading-relaxed" data-testid="ai-explain-text">{explain}</div>
            ) : (
              <p className="text-sm text-[var(--text-muted)]">Ask Aurum to explain the current market in {beginner ? "simple" : "advanced"} terms, citing the actual computed data.</p>
            )}
          </Card>
        </div>

        {/* Intelligence panel */}
        <div className="space-y-5">
          {/* Regime + MTF */}
          <Card className="p-5" data-testid="intelligence-panel">
            <SectionLabel>Market Regime</SectionLabel>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-xl font-bold" data-testid="market-regime">{snap.regime.regime}</span>
              <Pill>{snap.regime.confidence}% conf</Pill>
            </div>
            <div className="mt-2 space-y-1">
              {snap.regime.evidence.slice(0, 3).map((e, i) => (
                <div key={i} className="text-[11px] text-[var(--text-muted)]">• {e}</div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              {snap.mtf["4H"] ? [["4H", snap.mtf["4H"]], ["1H", snap.mtf["1H"]], ["15M", snap.mtf["15M"]], ["5M", snap.mtf["5M"]]].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-[var(--border2)] p-2.5">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{k} {k === "4H" ? "Bias" : k === "1H" ? "Structure" : k === "15M" ? "Setup" : "Trigger"}</div>
                  <div className="text-xs font-semibold mt-0.5">{v.regime}</div>
                </div>
              )) : <div className="col-span-2 text-xs text-[var(--text-muted)]">Multi-timeframe analysis paused — data unavailable.</div>}
            </div>
          </Card>

          {snap.custom_strategy && (
            <Card className="p-5" data-testid="custom-strategy-card">
              <div className="flex items-center justify-between mb-2">
                <SectionLabel>My Strategy · {snap.custom_strategy.name}</SectionLabel>
                <Pill color={snap.custom_strategy.matched ? "#21C58E" : "#98A2B3"}>{snap.custom_strategy.matched ? "MATCHED" : "NOT MATCHED"}</Pill>
              </div>
              <div className="space-y-1.5">
                {snap.custom_strategy.checks.map((c, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <span className="text-[var(--text-sec)]">{c.rule}</span>
                    <span className={c.ok ? "text-bull" : "text-[var(--text-muted)]"}>{c.ok ? "✓" : "✕"} <span className="tnum">{String(c.value)}</span></span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <SetupScoreGauge score={snap.score} />

          {/* News + session */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3"><Newspaper size={15} className="text-aurum" /><SectionLabel>News Risk</SectionLabel></div>
            <div className="flex items-center justify-between">
              <span className="font-semibold" style={{ color: snap.news.risk === "LOW" ? "#21C58E" : snap.news.risk === "MEDIUM" ? "#F5B942" : "#FF4D67" }} data-testid="news-risk">{snap.news.risk}</span>
              {snap.news.event && <span className="text-[11px] text-[var(--text-muted)]">next: {snap.news.event.title}</span>}
            </div>
            {snap.news.event && <NewsCountdown seconds={snap.news.event.seconds_until} title={snap.news.event.title} impact={snap.news.event.impact} />}
            <div className="mt-4 pt-4 border-t border-[var(--border2)] flex items-center gap-2 text-sm">
              <Clock size={15} className="text-aurum" />
              <span className="text-[var(--text-sec)]">Session:</span>
              <span className="font-semibold">{snap.session.active.join(" + ")}</span>
              {snap.session.overlap && <Pill color="#60A5FA">Overlap</Pill>}
            </div>
            <div className="text-[11px] text-[var(--text-muted)] mt-1">Next: {snap.session.next_session} in {snap.session.minutes_to_next}m</div>
          </Card>

          {/* Paper account */}
          {paper && (
            <Card className="p-5" data-testid="paper-account">
              <div className="flex items-center justify-between mb-3">
                <SectionLabel>Paper Account</SectionLabel>
                <Pill color="#98A2B3">SIMULATED</Pill>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Metric label="Balance" value={`$${paper.account.balance?.toFixed(2)}`} testId="paper-balance" />
                <Metric label="Equity" value={`$${paper.equity?.toFixed(2)}`} color={paper.unrealized_pnl >= 0 ? "#21C58E" : "#EF6262"} />
              </div>
              {paper.open_trades.length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="text-[11px] uppercase tracking-wider text-[var(--text-muted)]">Open positions</div>
                  {paper.open_trades.map((t) => (
                    <div key={t.id} className="flex items-center justify-between rounded-lg border border-[var(--border2)] px-3 py-2">
                      <div className="text-xs">
                        <span className={`font-semibold ${t.direction === "LONG" ? "text-bull" : "text-bear"}`}>{t.direction}</span>
                        <span className="tnum text-[var(--text-sec)]"> {t.lots} lots @ {t.entry}</span>
                      </div>
                      <button onClick={async () => { await api.post(`/paper/close/${t.id}`, {}); toast.success("Position closed & journaled."); refreshPaper(); }}
                        className="text-xs text-bear hover:underline" data-testid={`close-${t.id}`}>Close</button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          <button onClick={() => canApprove ? setShowChecklist(true) : toast.info("Entry conditions are not yet satisfied. Aurum recommends waiting.")}
            data-testid="approve-trade-btn"
            className={`w-full rounded-xl py-3 text-sm font-semibold transition-all ${canApprove ? "btn-gold hover:brightness-110" : "border border-[var(--border2)] text-[var(--text-muted)] cursor-not-allowed"}`}>
            {canApprove ? "Review & Approve Paper Trade" : "Approve Paper Trade (conditions not met)"}
          </button>

          <button onClick={() => snap.trade ? setShowLiveLog(true) : toast.info("No active setup to log yet.")}
            data-testid="log-live-trade-btn"
            className={`w-full rounded-xl py-3 text-sm font-semibold transition-all ${snap.trade ? "border border-aurum text-aurum hover:bg-[rgba(214,174,87,0.1)]" : "border border-[var(--border2)] text-[var(--text-muted)] cursor-not-allowed"}`}>
            I placed this on MT5 — log it
          </button>
        </div>
      </div>

      <ChecklistModal open={showChecklist} onOpenChange={setShowChecklist} snap={snap} mode="paper" onDone={() => { refreshPaper(); refresh(); }} />
      <ChecklistModal open={showLiveLog} onOpenChange={setShowLiveLog} snap={snap} mode="live" onDone={() => refresh()} />
    </div>
  );
}

function NewsCountdown({ seconds, title, impact }) {
  if (seconds == null) return null;
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600), m = Math.floor((abs % 3600) / 60);
  const color = impact === "EXTREME" ? "#FF4D67" : impact === "HIGH" ? "#F5B942" : "#60A5FA";
  return (
    <div className="mt-2 rounded-lg px-3 py-2 flex items-center justify-between" style={{ background: `${color}12`, border: `1px solid ${color}33` }} data-testid="news-card">
      <div>
        <div className="text-xs font-medium">{title}</div>
        <div className="text-[10px] uppercase tracking-wider" style={{ color }}>{impact} impact</div>
      </div>
      <div className="tnum text-sm font-semibold" style={{ color }}>{seconds < 0 ? "released" : `${h}h ${m}m`}</div>
    </div>
  );
}

const CHECKS = [
  ["regime", "Market regime identified"], ["htf", "Higher timeframe reviewed"],
  ["strategy", "Strategy valid for regime"], ["levels", "Key levels marked"],
  ["confirm", "Entry confirmation present"], ["news", "News reviewed"],
  ["stop", "Stop defined"], ["targets", "Targets defined"],
  ["rr", "Risk/reward acceptable"], ["size", "Position size calculated"],
  ["limit", "Daily risk limit available"], ["chase", "Trade is not chasing"],
];

function ChecklistModal({ open, onOpenChange, snap, onDone, mode = "paper" }) {
  const [checked, setChecked] = useState({});
  const [accept, setAccept] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const live = mode === "live";
  const all = CHECKS.every(([k]) => checked[k]) && accept;

  const approve = async () => {
    setSubmitting(true);
    try {
      const t = snap.trade;
      if (live) {
        await api.post("/journal", {
          direction: snap.setup.direction, strategy: snap.setup.strategy,
          entry: t.preferred_entry, stop: t.stop, tp1: t.tp1, tp2: t.tp2, tp3: t.tp3,
          lots: snap.sizing?.position_size_lots || null, regime: snap.regime.regime,
          setup_score: snap.score.score, news_risk: snap.news.risk,
          user_reasoning: note, source: "MT5",
        });
        toast.success("Live MT5 trade logged to your journal.");
      } else {
        await api.post("/paper/order", {
          direction: snap.setup.direction, entry: t.preferred_entry, stop: t.stop,
          tp1: t.tp1, tp2: t.tp2, tp3: t.tp3,
          lots: snap.sizing?.position_size_lots || 0.01, strategy: snap.setup.strategy,
          regime: snap.regime.regime, setup_score: snap.score.score, news_risk: snap.news.risk,
          case_for: snap.devil.case_for, case_against: snap.devil.case_against,
          user_reasoning: note,
        });
        toast.success("Paper trade executed & journaled.");
      }
      onOpenChange(false); setChecked({}); setAccept(false); setNote(""); onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save trade.");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[var(--card2)] border-[var(--border2)] text-[var(--text)]">
        <DialogHeader><DialogTitle>{live ? "Pre-Trade Checklist · Log MT5 Trade" : "Pre-Trade Checklist"}</DialogTitle></DialogHeader>
        {live && (
          <p className="text-xs text-[var(--text-muted)] -mt-2">Tick each item to confirm discipline, then log the trade you placed (or are about to place) on MetaTrader.</p>
        )}
        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {CHECKS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`check-${k}`}>
              <input type="checkbox" checked={!!checked[k]} onChange={(e) => setChecked({ ...checked, [k]: e.target.checked })}
                className="h-4 w-4 accent-[#D6AE57]" />
              {label}
            </label>
          ))}
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Your reasoning (optional)…"
          className="w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" rows={2} />
        <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer" data-testid="accept-risk">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="h-4 w-4 accent-[#D6AE57]" />
          I accept that this trade can lose.
        </label>
        <button onClick={approve} disabled={!all || submitting} data-testid="confirm-approve"
          className="btn-gold w-full rounded-lg py-2.5 text-sm font-semibold transition-all hover:brightness-110 disabled:opacity-50">
          {submitting ? "Saving…" : live ? "Log MT5 Trade" : "Approve Paper Trade"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
